import React, { useContext, useEffect, useState } from "react"
import { Accordion, Divider, Form, Icon, Message } from "semantic-ui-react"
import { discountType } from "../../constants"
import { CampProps, CampingStyleProps } from "../../interfaces"
import CampDiscountProps from "../../interfaces/CampDiscountProps"
import { displayPercentage } from "../../utils"
import Button from "../ui/Button"

import DiscountOption from "./DiscountOption"
import { BookerContext } from "../../contexts/BookerContext"
import { formatMoney, formatMoneyFromCents } from "../../utils/formatMoney"
import api from "../../api2"
import InputFieldWithLabel from "../Admin/Inputs/InputFieldWithLabel"
import Alert from "../ui/Alert"
import classNames from "../../utils/classNames"
import ICoupon from "../../interfaces/ICoupon"

interface ComponentProps {
  camp: CampProps
  discountRates: Record<string, CampDiscountProps>
  discountType: discountType
  setDiscountType: React.Dispatch<React.SetStateAction<discountType>>
  discountId: string
  setDiscountId: React.Dispatch<React.SetStateAction<string>>
  discountValid: boolean
  isLoading: boolean
}

const DiscountForm = (props: ComponentProps) => {
  const { secondaryColor, couponCode, setCouponCode } = useContext(BookerContext)

  const [isActive, setIsActive] = useState(props.discountType || props.discountId || false)
  const [radioOption, setRadioOption] = useState<discountType>()
  const [inputValue, setInputValue] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const filteredDiscountRates = props.discountRates
    ? Object.fromEntries(
        Object.entries(props.discountRates).filter(([_, discount]) => !(discount as any).deleted_at)
      )
    : {}

  const handleDiscountTypeChange = (e, { value }) => {
    setErrorMessage(null)
    props.setDiscountId("")
    props.setDiscountType("")
    setRadioOption(value)
    setInputValue("")
  }

  const handleInputChange = (e, { value }) => setInputValue(value)

  const applyDiscount = () => {
    props.setDiscountType(radioOption)
    props.setDiscountId(inputValue)
  }

  const removeDiscount = () => {
    setRadioOption(undefined)
    setInputValue("")

    props.setDiscountType(undefined)
    props.setDiscountId("")
  }

  useEffect(() => {
    if (props.discountValid) {
      setErrorMessage(null)
    } else {
      if (props.discountType) setErrorMessage("Please provide a valid membership ID")
    }
  }, [props.discountId, props.discountType])

  const handleFormClick = () => {
    setIsActive(!isActive)
  }

  const appliedMessageForDiscount = () => {
    const selectedDiscount = filteredDiscountRates[props.discountType]
    if (!selectedDiscount) return ""

    if (selectedDiscount.percent_off) {
      return `${selectedDiscount.name} discount of ${displayPercentage(selectedDiscount.percent_off)} has been applied to your total.`
    } else if (selectedDiscount.amount_off) {
      return `${selectedDiscount.name} discount of ${formatMoney(selectedDiscount.amount_off, props.camp.currency)} per night has been removed from your total.`
    } else if (selectedDiscount.amount_off_per_night_in_cents) {
      return `${selectedDiscount.name} discount of ${formatMoneyFromCents(selectedDiscount.amount_off_per_night_in_cents)} per night has been applied to your total.`
    } else if (selectedDiscount.flat_rate_in_cents) {
      return `${selectedDiscount.name} discounted rate of ${formatMoneyFromCents(selectedDiscount.flat_rate_in_cents, props.camp.currency)} per night has been applied to your total.`
    } else if (selectedDiscount.subtotal_off_in_cents) {
      return `${selectedDiscount.name} discount of ${formatMoneyFromCents(selectedDiscount.subtotal_off_in_cents, props.camp.currency)} has been applied to your total.`
    }
  }
  
  return (
    <>
      {filteredDiscountRates && Object.keys(filteredDiscountRates).length > 0 && (
        <Accordion styled fluid className="relative z-20">
          <Accordion.Title active={isActive} index={0} onClick={handleFormClick}>
            <Icon name="dropdown" />
            Add Discount
          </Accordion.Title>

          <Accordion.Content active={isActive}>
            <Form loading={props.isLoading} success={props.discountValid} className="flex flex-col xs:block">
              <p className="text-gray-500 mb-1 font-normal">
                If applicable, select a discount type
                {Object.values(filteredDiscountRates).find((x: CampDiscountProps) => x.require_membership_id)
                  ? ", and enter a valid membership ID, and click to apply."
                  : " and click to apply."}{" "}
                {props.camp.slug == "nickerson-beach-park"
                  ? "You must be a Nassau County Resident and present a valid Nassau County Drivers License upon check in to receive this discount"
                  : "Please have your membership ID available when you arrive."}
              </p>
              <Divider hidden />
              <Form.Group inline>
                <span className="ui small"></span>
                {Object.keys(filteredDiscountRates).map((type) => {
                  return (
                    <DiscountOption
                      key={type}
                      radioOption={radioOption}
                      name={filteredDiscountRates[type].name}
                      discountType={type}
                      onChange={handleDiscountTypeChange}
                    />
                  )
                })}
              </Form.Group>
              {filteredDiscountRates[radioOption]?.require_membership_id ? (
                <Form.Input
                  loading={props.isLoading}
                  fluid
                  icon={props.discountValid ? { name: "check circle", color: "green" } : { name: "user circle" }}
                  iconPosition="left"
                  label="Membership ID"
                  value={inputValue}
                  onChange={handleInputChange}
                  error={
                    !props.discountValid && errorMessage
                      ? {
                          content: errorMessage,
                          pointing: "above",
                        }
                      : null
                  }
                />
              ) : (
                <></>
              )}
              {props.discountType?.length && props.discountValid ? (
                <Alert kind="success" description={appliedMessageForDiscount()} className="max-w-md mb-2" />
              ) : (
                <></>
              )}
              <Button
                style={{ backgroundColor: secondaryColor }}
                className="hover:opacity-80 text-white mt-2.5"
                disabled={props.isLoading || !radioOption}
                onClick={applyDiscount}
              >
                Apply Discount
              </Button>
              <Button
                variant="none"
                style={{ marginLeft: 5 }}
                className="hover:opacity-80 underline text-red-500"
                disabled={props.isLoading || !radioOption}
                onClick={removeDiscount}
              >
                Remove Discount
              </Button>
            </Form>
          </Accordion.Content>
        </Accordion>
      )}
      
    </>
  )
}

export default DiscountForm
